import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Ticket, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { usePageSEO, SEO } from "@/lib/seo";
import SafeImg from "@/components/SafeImg";
import { RichText } from "@/components/RichText";
import CampaignCounter from "@/components/CampaignCounter";

// Editorial home — 10 CMS-driven sections. Data comes from /api/home; each
// section is a small component below that receives a normalised `s.data`
// object. Never hard-code copy or image URLs here — admins edit everything.

const useT = () => {
  const { lang } = useI18n();
  return (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";
};

// Compute CSS object-position from an image's `focal` (and `focal_mobile`)
// admins pick in the CMS. Falls back to centre when nothing is set.
const focalPos = (img, isMobile = false) => {
  const f = isMobile ? (img?.focal_mobile || img?.focal) : img?.focal;
  const x = typeof f?.x === "number" ? f.x : 50;
  const y = typeof f?.y === "number" ? f.y : 50;
  return `${x}% ${y}%`;
};

// Hook that returns true when the viewport is <= 767px (aligns with Tailwind's `md` breakpoint).
const useIsMobile = () => {
  const [m, setM] = useState(false);
  useEffect(() => {
    const q = window.matchMedia("(max-width: 767px)");
    const on = () => setM(q.matches);
    on();
    q.addEventListener ? q.addEventListener("change", on) : q.addListener(on);
    return () => (q.removeEventListener ? q.removeEventListener("change", on) : q.removeListener(on));
  }, []);
  return m;
};

const REGISTRY = {
  HeroChurch, OriginStory, SocialRunRecap, NextEventKB, RaffleModule,
  DenmarkSpain, JoinDanamark, ImpactTransparency, PartnersWall, FinalCTA,
};

export default function Home() {
  usePageSEO(SEO.home.title, SEO.home.description);
  const [sections, setSections] = useState(null);
  const [membershipUrl, setMembershipUrl] = useState("/get-involved");
  useEffect(() => {
    api.get("/home").then(r => setSections(r.data.sections || [])).catch(() => setSections([]));
    // Fetch the current global membership URL. When admin publishes /join and
    // hits "Point to /join" in Site Settings, this value flips to "/join" and
    // membership CTAs on Home follow automatically. Sections that opt out via
    // `use_global_membership_url: false` keep their own `cta_url` instead.
    api.get("/site-settings").then(r => {
      setMembershipUrl((r.data?.membership?.url) || "/get-involved");
    }).catch(() => {});
  }, []);

  if (sections === null) {
    return <div className="min-h-screen bg-cream" />;
  }
  return (
    <div className="bg-cream">
      {sections.map(s => {
        const Comp = REGISTRY[s.data.component];
        if (!Comp) return null;
        return <Comp key={s.id} d={s.data} membershipUrl={membershipUrl} />;
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════ HERO CHURCH ══════════════
function HeroChurch({ d }) {
  const t = useT();
  const img = d.images?.[0] || {};
  const isMobile = useIsMobile();
  const overlay = typeof d.overlay === "number" ? d.overlay : 0.7;
  return (
    <section className="relative min-h-[100vh] flex items-end overflow-hidden bg-ink" data-testid="home-hero">
      <img src={img.url} alt={t(img.alt)} className="absolute inset-0 w-full h-full object-cover"
           style={{ objectPosition: focalPos(img, isMobile) }}
           loading="eager" />
      <div className="absolute inset-0"
           style={{ background: `linear-gradient(to top, rgba(28,25,23,${Math.min(1, overlay + 0.25)}) 0%, rgba(28,25,23,${overlay}) 45%, rgba(28,25,23,${Math.max(0, overlay - 0.4)}) 100%)` }} />
      <div className="relative container-x max-editorial pb-16 md:pb-24 pt-32 w-full">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-[640px]">
          <div className="label-mono text-white/85 mb-6 tracking-[0.22em]" data-testid="hero-eyebrow">
            {t(d.eyebrow)}
          </div>
          <h1 data-testid="hero-title"
              className="font-editorial text-white text-[42px] sm:text-6xl md:text-[72px] lg:text-[86px] leading-[0.98] tracking-tighter">
            {t(d.headline)}
          </h1>
          <div className="mt-8 md:mt-10 text-white/90 text-base md:text-lg leading-[1.7] max-w-[540px]">
            <RichText text={t(d.body)} gapClassName="mt-5" />
          </div>
          <div className="mt-10 md:mt-12 flex flex-wrap gap-3">
            {d.cta_label && (
              <Link to={d.cta_url || "/"} data-testid="hero-cta-primary"
                    className="bg-terracotta hover:bg-terracotta-dark text-white px-7 md:px-9 py-3.5 md:py-4 text-sm tracking-[0.16em] transition-colors inline-flex items-center gap-2 uppercase">
                {t(d.cta_label)} <ArrowRight size={14} />
              </Link>
            )}
            {d.cta2_label && (
              <Link to={d.cta2_url || "/"} data-testid="hero-cta-secondary"
                    className="text-white/80 hover:text-white px-4 py-3.5 md:py-4 text-sm tracking-[0.16em] transition-colors uppercase underline underline-offset-8 decoration-white/30 hover:decoration-white">
                {t(d.cta2_label)}
              </Link>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════ ORIGIN STORY ═════════════
function OriginStory({ d }) {
  const t = useT();
  const images = d.images || [];
  return (
    <section className="container-x max-editorial py-24 md:py-36" data-testid="home-origin">
      <div className="grid md:grid-cols-12 gap-8 md:gap-12">
        <div className="md:col-span-5">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{t(d.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[34px] md:text-[52px] leading-[1.02] tracking-tighter">
            {t(d.headline)}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 md:pl-4">
          <div className="text-ink/80 text-base md:text-lg leading-[1.75]">
            <RichText text={t(d.body)} gapClassName="mt-5" />
          </div>
          {d.pull_quote && (
            <blockquote className="mt-10 md:mt-12 pl-5 border-l-2 border-terracotta">
              <p className="font-editorial text-2xl md:text-3xl italic leading-snug text-ink" data-testid="origin-quote">
                "{t(d.pull_quote)}"
              </p>
            </blockquote>
          )}
          {d.cta_label && (
            <Link to={d.cta_url || "/"} className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase border-b border-ink/40 pb-1 hover:border-terracotta hover:text-terracotta transition-colors">
              {t(d.cta_label)} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
      {images.length > 0 && (
        <div className="mt-14 md:mt-20 grid grid-cols-6 gap-3 md:gap-6">
          {images[0] && (
            <div className="col-span-6 md:col-span-4 aspect-[4/3] md:aspect-[16/9] overflow-hidden">
              <img src={images[0].url} alt={t(images[0].alt)} loading="lazy" style={{ objectPosition: focalPos(images[0]) }} className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[900ms]" />
            </div>
          )}
          {images[1] && (
            <div className="col-span-3 md:col-span-2 aspect-square md:aspect-[3/4] overflow-hidden">
              <img src={images[1].url} alt={t(images[1].alt)} loading="lazy" style={{ objectPosition: focalPos(images[1]) }} className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[900ms]" />
            </div>
          )}
          {images[2] && (
            <div className="col-span-3 md:col-span-2 md:col-start-3 md:mt-[-30%] aspect-square md:aspect-[4/5] overflow-hidden">
              <img src={images[2].url} alt={t(images[2].alt)} loading="lazy" style={{ objectPosition: focalPos(images[2]) }} className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[900ms]" />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════ SOCIAL RUN RECAP ═════════════
function SocialRunRecap({ d }) {
  const t = useT();
  const imgs = d.images || [];
  const hero = imgs.find(i => i.role === "hero") || imgs[0];
  const rest = imgs.filter(i => i !== hero).slice(0, 4);
  return (
    <section className="bg-ink text-white py-24 md:py-36" data-testid="home-social-run">
      <div className="container-x max-editorial">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{t(d.eyebrow)}</div>
            <h2 className="mt-4 font-editorial text-[36px] md:text-[58px] leading-[0.98] tracking-tighter">
              {t(d.headline)}
            </h2>
            <div className="mt-8 text-white/80 text-base md:text-lg leading-[1.8]">
              <RichText text={t(d.body)} gapClassName="mt-5" />
            </div>
            {d.stats && (
              <dl className="mt-10 grid grid-cols-3 gap-4 md:gap-6 border-t border-white/15 pt-8">
                {d.stats.map((s, i) => (
                  <div key={i} className="border-r border-white/15 last:border-r-0 pr-3">
                    <dt className="font-editorial text-xl md:text-2xl leading-tight">{s.value}</dt>
                    <dd className="label-mono text-white/60 tracking-widest text-[10px] mt-2">{t(s.label)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {d.cta_label && (
              <Link to={d.cta_url || "/"} data-testid="social-run-cta"
                    className="mt-10 inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase border-b border-white/40 pb-1 hover:border-terracotta hover:text-terracotta transition-colors">
                {t(d.cta_label)} <ArrowRight size={14} />
              </Link>
            )}
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <div className="grid grid-cols-6 gap-2 md:gap-3">
              {hero && (
                <div className="col-span-6 aspect-[4/3] md:aspect-[16/10] overflow-hidden">
                  <img src={hero.url} alt={t(hero.alt)} loading="lazy" style={{ objectPosition: focalPos(hero) }} className="w-full h-full object-cover" />
                </div>
              )}
              {rest.map((im, i) => (
                <div key={i} className={`overflow-hidden ${i === 0 ? "col-span-3 md:col-span-2 aspect-square" : i === 1 ? "col-span-3 md:col-span-2 aspect-square" : i === 2 ? "col-span-3 md:col-span-2 aspect-square" : "col-span-6 md:col-span-2 aspect-[4/3]"}`}>
                  <img src={im.url} alt={t(im.alt)} loading="lazy" style={{ objectPosition: focalPos(im) }} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[900ms]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════ NEXT EVENT ═══════════════
function NextEventKB({ d }) {
  const t = useT();
  const { lang } = useI18n();
  const img = d.images?.[0] || {};
  // Community Meeting — Live Raffle Draw is at 17:00 (Copenhagen time).
  // Address is intentionally omitted from the public page; practical details
  // are shared privately with confirmed attendees.
  const dateIso = "2026-10-25T13:00:00+02:00";
  const countdown = useCountdown(dateIso);
  const eyebrow = ({ en: "COMMUNITY MEETING · 25 OCTOBER 2026", da: "FÆLLESSKABSMØDE · 25. OKTOBER 2026", es: "ENCUENTRO COMUNITARIO · 25 DE OCTUBRE DE 2026" })[lang];
  const headline = ({
    en: "Community Meeting — together across borders.",
    da: "Fællesskabsmøde — sammen på tværs af grænser.",
    es: "Encuentro comunitario — juntos a través de las fronteras.",
  })[lang];
  const body = ({
    en: "The Community Meeting brings together the DANAmark membership, the live raffle draw and a shared dinner.\n\nSaturday 25 October 2026 · Community Meeting from 13:00 · Live Raffle Draw at 17:00 (Copenhagen time). Practical details are shared privately with confirmed attendees.",
    da: "Fællesskabsmødet samler DANAmarks medlemmer, live-lodtrækningen og en fælles middag.\n\nLørdag 25. oktober 2026 · Fællesskabsmøde fra kl. 13.00 · Live-lodtrækning kl. 17.00 (dansk tid). Praktiske detaljer deles privat med bekræftede deltagere.",
    es: "El Encuentro comunitario reúne a las personas asociadas a DANAmark, el sorteo en directo y una cena compartida.\n\nSábado 25 de octubre de 2026 · Encuentro comunitario desde las 13:00 · Sorteo en directo a las 17:00 (hora de Copenhague). Los detalles prácticos se comparten en privado con asistentes confirmados.",
  })[lang];
  const ctaLabel = ({ en: "See the Community Meeting", da: "Se Fællesskabsmødet", es: "Ver el Encuentro comunitario" })[lang];
  const timeNote = ({ en: "Community Meeting · 13:00 · Live draw · 17:00", da: "Fællesskabsmøde · 13.00 · Live-lodtrækning · 17.00", es: "Encuentro comunitario · 13:00 · Sorteo en directo · 17:00" })[lang];
  return (
    <section className="container-x max-editorial py-24 md:py-36" data-testid="home-next-event" id="campaign-finale">
      <div className="grid md:grid-cols-12 gap-8 md:gap-14 items-center">
        <div className="md:col-span-5 order-2 md:order-1">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]" data-testid="next-event-eyebrow">
            {eyebrow}
          </div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[58px] leading-[0.98] tracking-tighter">
            {headline}
          </h2>
          <div className="mt-6 text-ink/80 text-base md:text-lg leading-[1.75]">
            <RichText text={body} gapClassName="mt-5" />
          </div>
          {countdown && (
            <div className="mt-8 flex items-baseline gap-4 md:gap-6" aria-hidden>
              {countdown.map((c, i) => (
                <div key={i}>
                  <div className="font-editorial text-4xl md:text-5xl tracking-tighter tabular-nums">{c.value}</div>
                  <div className="label-mono text-ink/50 text-[9px] tracking-widest mt-1">{c.label}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/events/general-assembly-raffle-draw-2026" data-testid="next-event-cta"
                  className="bg-ink hover:bg-terracotta text-white px-7 py-3.5 text-sm tracking-[0.16em] transition-colors uppercase inline-flex items-center gap-2">
              {ctaLabel} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="md:col-span-7 order-1 md:order-2">
          <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden bg-ink/10">
            {img.url && <img src={img.url} alt={t(img.alt)} loading="lazy" style={{ objectPosition: focalPos(img) }} className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="font-editorial text-4xl md:text-5xl leading-[0.9]" data-testid="next-event-date">
                {formatDateLine(dateIso)}
              </div>
              <div className="mt-3 label-mono tracking-[0.2em] text-white/85">
                {timeNote}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════ RAFFLE MODULE ════════════
function RaffleModule({ d }) {
  const t = useT();
  const [cfg, setCfg] = useState(null);
  const [cfgError, setCfgError] = useState(false);
  useEffect(() => {
    api.get("/raffle/config")
      .then(r => { setCfg(r.data); setCfgError(false); })
      .catch(() => setCfgError(true));
  }, []);
  // Only render real counters once we have an authoritative response. While
  // loading or on error we render a neutral skeleton — never invented values
  // like "300 LEFT OF 300" or "0 SOLD".
  const hasCounts = Boolean(cfg?.counts);
  const total = cfg?.counts?.total || 0;
  const sold = cfg?.counts?.sold ?? 0;
  const available = cfg?.counts?.available ?? 0;
  const prizes = (cfg?.prizes || []).slice(0, d.featured_prize_count || 3);
  const img = d.images?.[0] || {};
  return (
    <section className="relative py-20 md:py-32 bg-terracotta/5 border-y border-terracotta/15" data-testid="home-raffle">
      <div className="container-x max-editorial grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        <div className="md:col-span-6">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">
            {t(d.eyebrow)}
          </div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[54px] leading-[0.98] tracking-tighter">
            {t(d.headline)}
          </h2>
          <div className="mt-6 text-ink/80 text-base md:text-lg leading-[1.75]" data-testid="home-raffle-body">
            <RichText text={t(d.body)} gapClassName="mt-5" />
          </div>
          {hasCounts ? (
            <div className="mt-8 flex items-end gap-6 md:gap-8" data-testid="raffle-counter">
              <div>
                <div className="font-editorial text-5xl md:text-6xl leading-[0.9] tabular-nums text-terracotta">{available}</div>
                <div className="label-mono text-ink/50 tracking-widest text-[10px] mt-2">
                  {t({ da: "TILBAGE AF", en: "LEFT OF", es: "QUEDAN DE" })} {total}
                </div>
              </div>
              <div className="flex-1 pb-2">
                <div className="h-1 bg-terracotta/15 rounded-full overflow-hidden">
                  <div className="h-full bg-terracotta transition-all duration-1000"
                       style={{ width: `${total > 0 ? Math.min(100, (sold / total) * 100) : 0}%` }} />
                </div>
                <div className="mt-2 label-mono text-ink/50 tracking-widest text-[9px]">
                  {sold} {t({ da: "SOLGT", en: "SOLD", es: "VENDIDOS" })}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-8 flex items-end gap-6 md:gap-8"
                 data-testid={cfgError ? "raffle-counter-error" : "raffle-counter-skeleton"}
                 aria-hidden="true">
              <div>
                <div className="h-[52px] md:h-[64px] w-[88px] md:w-[108px] bg-terracotta/10 rounded-sm animate-pulse" />
                <div className="mt-2 h-[10px] w-[72px] bg-ink/5 rounded-sm" />
              </div>
              <div className="flex-1 pb-2">
                <div className="h-1 bg-terracotta/10 rounded-full" />
                <div className="mt-2 h-[9px] w-[48px] bg-ink/5 rounded-sm" />
              </div>
            </div>
          )}
          <Link to={d.cta_url || "/raffle#buy"} data-testid="raffle-cta"
                className="mt-10 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-[0.18em] uppercase transition-colors">
            <Ticket size={16} strokeWidth={1.5} /> {t(d.cta_label)}
          </Link>
        </div>
        <div className="md:col-span-6">
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-6 aspect-[4/3] overflow-hidden">
              {img.url && <img src={img.url} alt={t(img.alt)} loading="lazy" style={{ objectPosition: focalPos(img) }} className="w-full h-full object-cover" />}
            </div>
            {prizes.length > 0 && (
              <div className="col-span-6 grid grid-cols-3 gap-3 mt-2">
                {prizes.map(p => (
                  <div key={p.id} className="bg-white border border-line/50 p-3" data-testid={`raffle-prize-${p.id}`}>
                    <div className="aspect-square overflow-hidden mb-2">
                      <SafeImg src={p.image} alt={t(p.image_alt || {})} name={t(p.title || {})}
                               className="w-full h-full object-cover" fallbackClassName="w-full h-full" />
                    </div>
                    <div className="label-mono text-terracotta text-[9px] tracking-widest truncate">{p.donor}</div>
                    <div className="font-editorial text-sm leading-tight mt-1 line-clamp-2">{t(p.title || {})}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════ DENMARK / SPAIN ══════════════
function DenmarkSpain({ d }) {
  const t = useT();
  const img = d.images?.[0] || {};
  return (
    <section className="relative py-0" data-testid="home-denmark-spain">
      <div className="relative min-h-[70vh] flex items-center overflow-hidden bg-ink">
        {img.url && <img src={img.url} alt={t(img.alt)} loading="lazy" style={{ objectPosition: focalPos(img) }} className="absolute inset-0 w-full h-full object-cover opacity-70" />}
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/30" />
        <div className="relative container-x max-editorial py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{t(d.eyebrow)}</div>
            <h2 className="mt-4 font-editorial text-white text-[36px] md:text-[58px] leading-[0.98] tracking-tighter">
              {t(d.headline)}
            </h2>
            <div className="mt-8 text-white/85 text-base md:text-lg leading-[1.8] max-w-[620px]">
              <RichText text={t(d.body)} gapClassName="mt-5" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════ JOIN DANAMARK ════════════
function JoinDanamark({ d, membershipUrl }) {
  const t = useT();
  const img = d.images?.[0] || {};
  // Default to using the global membership URL — so when the admin flips
  // `settings.membership.url` from "/get-involved" to "/join", this button
  // follows automatically. Opt-out per-section by setting
  // `use_global_membership_url: false` in the Home CMS.
  const useGlobal = d.use_global_membership_url !== false;
  const ctaUrl = useGlobal ? (membershipUrl || "/get-involved") : (d.cta_url || "/get-involved");
  return (
    <section className="container-x max-editorial py-24 md:py-36" data-testid="home-join">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          {img.url && (
            <div className="aspect-[4/5] md:aspect-square overflow-hidden">
              <img src={img.url} alt={t(img.alt)} loading="lazy" style={{ objectPosition: focalPos(img) }} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
        <div className="md:col-span-6 md:pl-6">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{t(d.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[54px] leading-[0.98] tracking-tighter">
            {t(d.headline)}
          </h2>
          <div className="mt-6 text-ink/80 text-base md:text-lg leading-[1.8]">
            <RichText text={t(d.body)} gapClassName="mt-5" />
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {d.cta_label && (
              <Link to={ctaUrl} data-testid="join-cta-primary"
                    className="bg-ink hover:bg-terracotta text-white px-7 py-3.5 text-sm tracking-[0.16em] transition-colors uppercase inline-flex items-center gap-2">
                <Heart size={14} /> {t(d.cta_label)}
              </Link>
            )}
            {d.cta2_label && (
              <Link to={d.cta2_url || "/get-involved"} className="text-ink/70 hover:text-ink px-4 py-3.5 text-sm tracking-[0.16em] uppercase underline underline-offset-8 decoration-ink/30 hover:decoration-ink">
                {t(d.cta2_label)}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════ IMPACT / TRANSPARENCY ════════════
function ImpactTransparency({ d }) {
  const t = useT();
  return (
    <section className="bg-cream border-y border-ink/10 py-20 md:py-28" data-testid="home-impact">
      <div className="container-x max-editorial">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-5">
            <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{t(d.eyebrow)}</div>
            <h2 className="mt-4 font-editorial text-[34px] md:text-[52px] leading-[0.98] tracking-tighter">
              {t(d.headline)}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="text-ink/80 text-base md:text-lg leading-[1.8]">
              <RichText text={t(d.body)} gapClassName="mt-5" />
            </div>
            {d.stats && (
              <dl className="mt-10 space-y-6">
                {d.stats.map((s, i) => (
                  <div key={i} className="border-t border-ink/15 pt-4 flex flex-wrap items-baseline justify-between gap-3">
                    <dt className="font-editorial text-3xl md:text-4xl tracking-tight">{s.value}</dt>
                    <dd className="label-mono text-ink/60 tracking-widest text-[10px] uppercase">{t(s.label)}</dd>
                  </div>
                ))}
              </dl>
            )}
            {d.cta_label && (
              <Link to={d.cta_url || "/impact"} data-testid="impact-cta"
                    className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase border-b border-ink/40 pb-1 hover:border-terracotta hover:text-terracotta transition-colors">
                {t(d.cta_label)} <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════ PARTNERS WALL ════════════
// ─── Partners marquee tuning ─────────────────────────────────────────────
// Central knob so speed can be adjusted without touching the component.
// Feel of the animation is driven by "seconds per logo": with 5 logos and
// 2.8s each, one full loop lasts 14s — dynamic but comfortably readable.
// Lower value → faster (harder to read). Higher value → slower (corporate).
const PARTNERS_MARQUEE_SECS_PER_LOGO = 2.8;
const PARTNERS_MARQUEE_MIN_DURATION = 14;   // s — floor for very short lists
const PARTNERS_MARQUEE_MAX_DURATION = 60;   // s — ceiling for very long lists

function PartnersWall({ d }) {
  const t = useT();
  const { lang } = useI18n();
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api.get("/home/partners").then(r => setPartners(r.data.partners || [])).catch(() => setPartners([]));
  }, []);
  const seeAllLabel = ({ en: "See all partners", da: "Se alle partnere", es: "Ver todos los colaboradores" })[lang] || "See all partners";
  return (
    <section className="container-x max-editorial py-20 md:py-28" data-testid="home-partners">
      <div className="max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{t(d.eyebrow)}</div>
        <h2 className="mt-4 font-editorial text-[32px] md:text-[46px] leading-[0.98] tracking-tighter">
          {t(d.headline)}
        </h2>
      </div>
      {partners.length > 0 ? (
        <PartnersMarquee partners={partners} lang={lang} />
      ) : (
        <div className="mt-10 text-sm text-ink/50 italic">—</div>
      )}
      <div className="mt-10 md:mt-12">
        <Link to="/get-involved#partners" data-testid="home-partners-see-all"
              className="inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase border-b border-ink/40 pb-1 hover:border-terracotta hover:text-terracotta transition-colors">
          {seeAllLabel} <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}

// Editorial infinite marquee. Renders the list twice back-to-back and
// translates the row from 0 → -50%; because the two halves are identical,
// the loop is seamless. Pauses on hover (desktop) and collapses into a
// static grid when the user has `prefers-reduced-motion: reduce`.
function PartnersMarquee({ partners, lang }) {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const q = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(q.matches);
    on();
    q.addEventListener ? q.addEventListener("change", on) : q.addListener(on);
    return () => (q.removeEventListener ? q.removeEventListener("change", on) : q.removeListener(on));
  }, []);
  const altFor = (p) => (p.alt?.[lang] || p.alt?.en || p.name || "");
  if (reduced) {
    return (
      <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 items-center" data-testid="partners-marquee-static">
        {partners.map(p => (
          <Link key={p.id} to={`/get-involved#partner-${p.anchor}`} data-testid={`partner-logo-${p.anchor}`}
                className="flex items-center justify-center h-16 md:h-20 grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-500">
            <SafeImg src={p.logo} alt={altFor(p)} name={p.name}
                     className="max-h-full max-w-full object-contain" fallbackClassName="w-full h-full" />
          </Link>
        ))}
      </div>
    );
  }
  // The animation duration scales with the list length so the visual speed
  // stays roughly constant regardless of how many logos are shown. Values
  // live in the tuning constants above so they can be adjusted globally.
  const durationSec = Math.min(
    PARTNERS_MARQUEE_MAX_DURATION,
    Math.max(PARTNERS_MARQUEE_MIN_DURATION, partners.length * PARTNERS_MARQUEE_SECS_PER_LOGO),
  );
  return (
    <div className="mt-14 relative overflow-hidden partners-marquee-wrap" data-testid="partners-marquee">
      <div className="partners-marquee-track flex gap-14 md:gap-20 items-center"
           style={{ animationDuration: `${durationSec}s` }}>
        {[...partners, ...partners].map((p, i) => {
          const isDuplicate = i >= partners.length;
          return (
            <Link key={`${p.id}-${i}`} to={`/get-involved#partner-${p.anchor}`}
                  data-testid={isDuplicate ? undefined : `partner-logo-${p.anchor}`}
                  aria-label={isDuplicate ? undefined : p.name}
                  aria-hidden={isDuplicate ? "true" : undefined}
                  tabIndex={isDuplicate ? -1 : undefined}
                  className="shrink-0 flex items-center justify-center h-14 md:h-20 min-w-[110px] md:min-w-[160px] grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-500">
              <SafeImg src={p.logo} alt={isDuplicate ? "" : altFor(p)} name={p.name}
                       className="max-h-full max-w-[180px] object-contain" fallbackClassName="h-full w-full" />
            </Link>
          );
        })}
      </div>
      {/* soft edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-24 bg-gradient-to-r from-cream to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-24 bg-gradient-to-l from-cream to-transparent" />
    </div>
  );
}

// ═══════════════════════════════════════════════ FINAL CTA ════════════════
function FinalCTA({ d, membershipUrl }) {
  const t = useT();
  const img = d.images?.[0] || {};
  const isMobile = useIsMobile();
  const useGlobal = d.use_global_membership_url !== false;
  const ctaUrl = useGlobal ? (membershipUrl || "/get-involved") : (d.cta_url || "/get-involved");
  return (
    <section className="relative overflow-hidden bg-ink" data-testid="home-final">
      {img.url && <img src={img.url} alt={t(img.alt)} loading="lazy" style={{ objectPosition: focalPos(img, isMobile) }} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/80 to-ink" />
      <div className="relative container-x max-editorial py-28 md:py-40 text-center">
        <div className="label-mono text-terracotta tracking-[0.24em] text-[10px] md:text-[11px]">{t(d.eyebrow)}</div>
        <h2 className="mt-6 font-editorial text-white text-[40px] md:text-[64px] leading-[0.98] tracking-tighter whitespace-pre-line">
          {t(d.headline)}
        </h2>
        <div className="mt-6 text-white/70 text-base md:text-lg italic">
          <RichText text={t(d.body)} gapClassName="mt-4" />
        </div>
        {d.cta_label && (
          <Link to={ctaUrl} data-testid="final-cta"
                className="mt-12 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-9 py-4 text-sm tracking-[0.2em] transition-colors uppercase">
            {t(d.cta_label)} <ArrowUpRight size={16} />
          </Link>
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────── helpers ──────────────────
function formatDateLine(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const day = d.getDate();
    const mm = d.toLocaleString("en", { month: "short" }).toUpperCase();
    const yy = d.getFullYear();
    return `${day} ${mm} ${yy}`;
  } catch { return iso; }
}

function useCountdown(iso) {
  const target = useMemo(() => (iso ? new Date(iso).getTime() : null), [iso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (days > 365 || diff === 0) return null;
  return [
    { value: days, label: "DAYS" },
    { value: String(hours).padStart(2, "0"), label: "HRS" },
    { value: String(mins).padStart(2, "0"), label: "MIN" },
  ];
}
